'use client';

import { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, ArrowRight, ChevronRight, LockKeyhole } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Scrollbar } from '@radix-ui/react-scroll-area';
import { Separator } from '../../components/ui/separator';
import { Card } from '../../components/ui/card';
import LiveSession from '../../../public/Icons/LiveSession.png';
import FormulaSheets from '../../../public/Icons/FormulaSheets.png';
import ExportPDF from '../../../public/Icons/ExportPDF.png';
import RevisionNotes from '../../../public/Icons/RevisionNotes.png';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { DarkModeToggle } from '../../Layout/DarkModeToggle';
import { CalendarBlankIcon, CheckCircleIcon, ClockIcon, QuestionIcon } from '@phosphor-icons/react';
import SyllabusModal from '../../components/modals/SyllabusModal';
import TestListingLoader from '../../components/loaders/TestListingLoader';

// Separate component for test content that will re-render independently
function TestContent({ packId, selectedCategory, onViewSyllabus, onAttemptTest, categories }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTests = async () => {
      if (!selectedCategory || !packId) {
        setTests([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`/api/packs/v2/dashboard/${packId}/${selectedCategory}/tests`);
        const responseData = response.data.data;
        const allTests = responseData.subgroups.flatMap((sg) =>
          sg.tests.map((t) => ({
            id: t.test._id,
            title: t.test.title,
            description: t.test.description,
            syllabus: t.test.syllabus,
            questions: t.test.totalQuestions,
            duration: t.test.totalTime,
            marks: t.test.totalMarks,
            analysisVideo: t.test.analysisVideo,
          })),
        );
        setTests(allTests);
        setError(null);
      } catch (err) {
        setError('Failed to fetch tests. Please try again later.');
        setTests([]);
        console.error('Error fetching tests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [packId, selectedCategory]);

  const selectedCategoryData = useMemo(
    () => categories.find((category) => category.groupId === selectedCategory),
    [categories, selectedCategory],
  );

  if (loading) {
    return <TestListingLoader isLocked />;
  }

  if (error) {
    return (
      <div className="flex-1 px-4 lg:px-0">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 lg:px-0">
      <h2 className="text-2xl font-bold mb-6">{selectedCategoryData?.title}</h2>
      <div className="space-y-4">
        {tests.map((test) => (
          <Card
            key={test.id}
            className="border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] rounded-lg shadow-none py-0 overflow-hidden active:scale-95 hover:scale-[1.02] transition-transform duration-300 ease-in-out cursor-pointer"
            onClick={() => onAttemptTest(test.id)}
          >
            <div className="flex flex-col w-full">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-2 lg:px-5 py-2 lg:py-4">
                <div className="flex items-center w-full lg:w-fit gap-5">
                  <div className="bg-[var(--q3-danger-normal)] p-2 rounded-full">
                    <LockKeyhole className="h-5 w-5 text-[var(--q3-danger-light)]" />
                  </div>
                  <div className="flex flex-col w-full lg:w-fit">
                    <div className="flex items-start justify-start gap-2 w-full lg:w-fit text-left">
                      <h3 className="font-bold text-md">{test.title}</h3>
                      {test.mustDo && (
                        <Badge className="bg-pink-50 text-pink-600 border border-pink-200 hover:bg-pink-50">
                          Must Do
                        </Badge>
                      )}
                    </div>
                    <div className=" text-start mt-1 text-sm text-gray-500">
                      {test.description}
                      {test.isVideoSolutionAvailable && (
                        <>
                          {' • '}
                          <span className="block sm:inline">Video solution available</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex w-full lg:w-fit flex-row gap-2">
                  <Button
                    className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewSyllabus(test.title, test.syllabus);
                    }}
                  >
                    View Syllabus
                  </Button>
                  <Button
                    className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)]"
                    onClick={() => onAttemptTest(test.id)}
                  >
                    Attempt Test
                  </Button>
                </div>
              </div>
              <div className="px-2 lg:pl-18 lg:pr-4 flex justify-between items-center py-2 bg-[var(--q3-surface-dimmer)] flex-wrap">
                <div className="flex justify-between items-center w-full flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1 text-sm">
                      <QuestionIcon size={20} /> <strong>{test.questions} Qs</strong>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <ClockIcon size={20} />
                      <strong>{test.duration} Min</strong>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <CheckCircleIcon size={20} /> Score: <strong>{test.marks}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function LockedTestDetails() {
  const [packTitle, setPackTitle] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedulePdfUrl, setSchedulePdfUrl] = useState('');
  const [showSyllabusDialog, setShowSyllabusDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', syllabus: '' });
  const [moreDetails, setmoreDetails] = useState('');

  const { packId } = useParams();
  const navigate = useNavigate();

  // Memoize resources to prevent unnecessary re-renders
  const resources = useMemo(
    () => [
      {
        isLocked: false,
        title: 'Tests Schedule',
        icon: <CalendarBlankIcon className="h-6 w-6 text-[var(--q3-violet-normal)]" />,
        bgColor: 'bg-[var(--q3-violet-light)]',
        textColor: 'text-[var(--q3-violet-default)]',
        href: schedulePdfUrl,
        target: '_blank',
      },
      {
        isLocked: true,
        title: 'Live Session Recording',
        icon: <img src={LiveSession || '/placeholder.svg'} className="h-6 w-6" />,
        bgColor: 'bg-[var(--q3-danger-light)]',
        href: '#live-session',
      },
      {
        isLocked: true,
        title: 'Formula Sheets',
        icon: <img src={FormulaSheets || '/placeholder.svg'} className="h-6 w-6" />,
        bgColor: 'bg-[var(--q3-fuchsia-light)]',
        href: '#formula',
      },
      {
        isLocked: true,
        title: 'Revision Notes',
        icon: <img src={RevisionNotes || '/placeholder.svg'} className="h-6 w-6" />,
        bgColor: 'bg-[var(--q3-emerald-light)]',
        href: '#notes',
      },
      {
        isLocked: true,
        title: 'Important PDFs',
        icon: <img src={ExportPDF || '/placeholder.svg'} className="h-6 w-6" />,
        bgColor: 'bg-[var(--q3-orange-light)]',
        href: '#pdfs',
      },
    ],
    [schedulePdfUrl],
  );

  // Only fetch pack details once on component mount
  useEffect(() => {
    const fetchPackDetails = async () => {
      if (!packId) {
        setInitialLoading(false);
        setError('Pack ID not found in URL.');
        return;
      }

      setInitialLoading(true);
      try {
        const response = await axios.get(`/api/packs/v2/dashboard/${packId}/basic`);
        setPackTitle(response.data.data.title);
        setSchedulePdfUrl(response.data.data.schedulePdf);
        setmoreDetails(response.data.data.detailsUrl);

        const fetchedCategories = response.data.data.category.flatMap((c) =>
          c.groups.map((g) => ({ ...g, parentTitle: c.title })),
        );
        setCategories(fetchedCategories);

        if (fetchedCategories.length > 0) {
          setSelectedCategory(fetchedCategories[0].groupId);
        }
        setError(null);
      } catch (err) {
        setError('Failed to fetch pack details. Please try again later.');
        console.error('Error fetching pack details:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchPackDetails();
  }, [packId]);

  const handleViewSyllabus = (title, syllabus) => {
    setDialogContent({ title, syllabus });
    setShowSyllabusDialog(true);
  };

  const handleAttemptTest = (testId) => {
    navigate(`/v3/${packId}/${testId}/locked-test-details`);
  };

  if (initialLoading) {
    return (
      <div className="p-10">
        <TestListingLoader />
      </div>
    );
  }

  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  return (
    <div className="flex justify-center relative">
      <div className="md:hidden">
        <DarkModeToggle />
      </div>
      <div className="w-full flex flex-col items-center">
        {/* Header - This section won't re-render when selectedCategory changes */}
        <div className="px-4 lg:px-0 w-full lg:w-9/12 flex flex-col md:flex-row gap-3 lg:gap-0 justify-between items-start lg:items-center py-4">
          <div className="flex items-center gap-3">
            <div
              onClick={() => navigate('/v2/home')}
              className="w-fit cursor-pointer p-2 bg-[var(--q3-surface-dimmer)] rounded-full"
            >
              <ArrowLeft />
            </div>
            <h1 className="text-lg font-medium">{packTitle}</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.open(moreDetails, '_blank')} className="gap-2 rounded-full">
              View More Details <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => window.open(moreDetails, '_blank')}
              className="gap-2 rounded-full bg-[var(--q3-danger-normal)]"
            >
              Purchase <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Separator orientation="horizontal" />

        <div className="w-full lg:w-9/12 mt-8 flex flex-col md:flex-row gap-6">
          {/* Desktop sidebar - Only re-renders when categories change */}
          <div className="hidden md:block w-64 pl-0 md:pl-4 lg:pl-0 shrink-0">
            <h2 className="text-lg font-bold mb-7">All Tests</h2>
            <div className="space-y-2">
              {categories.map((category) => (
                <Button
                  key={category.groupId}
                  variant={selectedCategory === category.groupId ? 'default' : 'ghost'}
                  className={cn('w-full justify-start text-left font-normal')}
                  onClick={() => setSelectedCategory(category.groupId)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{category.title}</span>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </Button>
              ))}
            </div>
          </div>

          {/* Right content area */}
          <div className="flex-1 flex flex-col">
            {/* Resources section - positioned just above test content */}
            <div className="mb-6 px-4 md:px-0">
              <div className="text-lg font-medium mb-4">Resources</div>
              <div className="flex gap-4 lg:gap-8 overflow-x-auto">
                {resources.map((resource) => (
                  <div key={resource.title} className="flex-shrink-0 text-center group">
                    <a
                      href={resource.href}
                      target={resource.target}
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!resource.href || resource.href === '#') {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Card
                        className={`${resource.bgColor} border-none shadow-none flex items-center justify-center h-13 rounded-full mx-auto w-28 transition-transform`}
                      >
                        <div>{resource.isLocked ? <LockKeyhole /> : resource.icon}</div>
                      </Card>
                      <p className="mt-2 text-base font-medium text-center w-28 mx-auto break-words">
                        {resource.title}
                      </p>
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile tabs - Only re-renders when categories change */}
            <div className="md:hidden w-full px-4 mb-6">
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex space-x-2 p-1">
                  {categories.map((category) => (
                    <Badge
                      key={category.groupId}
                      variant={selectedCategory === category.groupId ? 'default' : 'outline'}
                      className="cursor-pointer py-2 px-4"
                      onClick={() => setSelectedCategory(category.groupId)}
                    >
                      {category.title}
                    </Badge>
                  ))}
                </div>
                <Scrollbar orientation="horizontal" />
              </ScrollArea>
            </div>

            {/* Tests content - Only this component re-renders when selectedCategory changes */}
            <TestContent
              packId={packId}
              selectedCategory={selectedCategory}
              onViewSyllabus={handleViewSyllabus}
              onAttemptTest={handleAttemptTest}
              categories={categories}
            />
          </div>
        </div>
      </div>

      {/* Syllabus Dialog */}
      <SyllabusModal open={showSyllabusDialog} onOpenChange={setShowSyllabusDialog} dialogContent={dialogContent} />
    </div>
  );
}
